import React from 'react';

import { Avatar } from 'antd';
import classNames from 'classnames';
import styled from 'styled-components';

import { COMMUNITIES_AVATAR_SIZE } from 'components/Overview/constants';

import useRecommendationDetailsContext from './RecommendationDetailsContext';
import { splitAddress } from '../CommunityInfo/utils';

const Label = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
  padding-bottom: 3px;
  color: var(--text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Street = styled.div`
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--text-primary);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 2px 0;
`;

const State = styled.div`
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--gray-dark);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-top: 4px;
`;

const StyledAvatar = styled(Avatar)`
  color: var(--neutral-purple);
  background-color: var(--neutral-purple-light);
  border: 1px solid var(--neutral-purple);

  span {
    font-size: 10px;
  }
`;

const IconContainer = styled.div`
  width: 30px;
  height: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  padding: 1px;
  background-image: linear-gradient(var(--white), var(--white)),
    linear-gradient(to bottom, var(--competitor-purple), var(--negative-orange) 50%, var(--flamingo-pink));
  background-origin: border-box;
  background-clip: content-box, border-box;
`;

const MenuItem = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 16px;
  min-width: 340px;
  font-size: 12px;
  padding: 32px 30px 32px 30px;
  border-bottom: 1px solid var(--line-gray);
  cursor: pointer;

  background-color: #fff;

  &:hover {
    background-color: var(--active-blue);
    ${Label} {
      color: var(--white);
    }
    ${Street} {
      color: var(--white);
    }
    ${State} {
      color: var(--white);
      opacity: 0.6;
    }
  }

  &.selected {
    background-color: var(--active-blue);
    ${Label} {
      color: var(--white);
    }
    ${Street} {
      color: var(--white);
    }
    ${State} {
      color: var(--white);
      opacity: 0.6;
    }
  }
`;

type Props = {
  id: number;
  to: string;
  pattern?: string;
  label: string;
  description: string;
  iconSrc?: string;
  isOverallView?: boolean;
};

const RecommendationDetailsSidebarItem = ({ id, iconSrc, label, description, isOverallView }: Props) => {
  const { selectedItem, setSelectedItem, filters, updateFilters } = useRecommendationDetailsContext();

  const menuItemClassnames = classNames({
    selected: id == selectedItem,
  });
  const { street, restAddress } = isOverallView ? { street: '', restAddress: description } : splitAddress(description);

  const handleItemSelected = () => {
    setSelectedItem(id);
    updateFilters({ ...filters, communities: [] });
  };

  return (
    <MenuItem className={menuItemClassnames} onClick={handleItemSelected}>
      <div>
        {iconSrc ? (
          <IconContainer>
            <img src={iconSrc} alt="ai-blob" height="20px" width="20px" />
          </IconContainer>
        ) : (
          <StyledAvatar size={COMMUNITIES_AVATAR_SIZE}>{label ? label[0] : 'n/a'}</StyledAvatar>
        )}
      </div>
      <div title={`${label} | ${description}`}>
        <Label>{label}</Label>
        {street && <Street>{street}</Street>}
        <State>{restAddress}</State>
      </div>
    </MenuItem>
  );
};

export default RecommendationDetailsSidebarItem;
